import { useQueryClient } from '@tanstack/react-query';
import { apiService } from '@/lib/api';
import type { EstimateDto, JobDTO, PaymentDTO, PaymentOperationDTO, PaymentStatus, PaymentTarget } from '@/types';

/**
 * Something that can be paid — a job or an estimate. Lets the Take Payment modal and the
 * payments card serve both without knowing which one they are looking at.
 */
export interface Payable {
    target: PaymentTarget;
    companyId?: string;
    /** Invoice / estimate number, shown on the Tap to Pay receipt. */
    documentNumber?: string;
    /** Total including tax — what payments are measured against. */
    total: number;
    payments: PaymentDTO[];
    /** Server-computed status; callers fall back to a local calculation when absent. */
    paymentStatus?: PaymentStatus;
    /** Deposit asked of the customer; null = none. Due now is amountDueNow(total, paid, depositAmount). */
    depositAmount?: number | null;
    /** Sets (or with null clears) the deposit. */
    saveDeposit: (amount: number | null) => Promise<void>;
    /** Prefills the digital receipt Apple requires after every Tap to Pay transaction (§5.9). */
    receiptEmail?: string;
    /** Saves recorded-payment add/remove operations. */
    savePayments: (ops: PaymentOperationDTO[], newStatus: PaymentStatus) => Promise<void>;
    /**
     * A card payment or refund went through at the provider. Our record only changes when its
     * webhook lands a moment later, so this refreshes the payable once it has.
     */
    awaitProviderUpdate: () => void;
}

const PROVIDER_UPDATE_POLL_MS = 2000;
const PROVIDER_UPDATE_POLL_ATTEMPTS = 15;

/**
 * Re-fetches until the payment list changes (a card payment or a refund each add a row), then applies it.
 * Jobs also get a realtime JobUpdated event, but it isn't guaranteed to arrive (and estimates have none).
 * ponytail: fixed 30s window; a realtime event per payable would replace this if it's ever too slow.
 */
function pollForPaymentChange<T extends { payments?: PaymentDTO[] }>(
    paymentCountBefore: number,
    fetchFresh: () => Promise<T>,
    apply: (fresh: T) => void,
) {
    void (async () => {
        for (let i = 0; i < PROVIDER_UPDATE_POLL_ATTEMPTS; i++) {
            await new Promise((resolve) => setTimeout(resolve, PROVIDER_UPDATE_POLL_MS));
            try {
                const fresh = await fetchFresh();
                if ((fresh.payments?.length ?? 0) !== paymentCountBefore) {
                    apply(fresh);
                    return;
                }
            } catch {
                // transient — keep polling
            }
        }
    })();
}

export function useJobPayable(job: JobDTO, onJobUpdated?: (updatedJob?: JobDTO) => void): Payable {
    const queryClient = useQueryClient();

    const applyJob = (updatedJob: JobDTO) => {
        // Update the jobs caches directly with the returned data (no refetch needed)
        queryClient.setQueriesData({ queryKey: ['jobs'] }, (existing: any) => {
            if (!existing) return existing;
            const isArray = Array.isArray(existing);
            const items = isArray ? existing : existing.jobs;
            if (!Array.isArray(items)) return existing;
            const updatedItems = items.map((j: JobDTO) => (j.id === updatedJob.id ? updatedJob : j));
            if (isArray) return updatedItems;
            return { ...existing, jobs: updatedItems };
        });
        // Also update the single-job cache used by JobDetailsModal
        queryClient.setQueryData(['job', updatedJob.id], updatedJob);
        onJobUpdated?.(updatedJob);
    };

    return {
        target: { kind: 'job', id: job.id },
        companyId: job.company?.id,
        documentNumber: job.invoice_number,
        total: job.total || 0,
        payments: job.payments ?? [],
        paymentStatus: job.payment_status,
        depositAmount: job.deposit_amount,
        saveDeposit: async (amount) => {
            applyJob(await apiService.mergePatchJob(job.id, { deposit_amount: amount }));
        },
        receiptEmail: job.contacts?.find((c) => c.contact?.email)?.contact.email,
        savePayments: async (ops, newStatus) => {
            // payment_status rides the same PATCH so the list shows it without a refetch
            applyJob(await apiService.mergePatchJob(job.id, {
                payment_operations: ops,
                payment_status: newStatus,
            }));
        },
        awaitProviderUpdate: () => {
            // Existing contract: parents treat `undefined` as "Stripe path — refetch" (TakePaymentButton callers).
            onJobUpdated?.(undefined);
            pollForPaymentChange(job.payments?.length ?? 0, () => apiService.getJobById(job.id), (fresh) => {
                applyJob(fresh);
                // Stripe payment details (refunded amount) shown from the payments card
                queryClient.invalidateQueries({ queryKey: ['stripePayment'] });
            });
        },
    };
}

export function useEstimatePayable(estimate: EstimateDto): Payable {
    const queryClient = useQueryClient();

    const setEstimate = (updated: EstimateDto) => {
        queryClient.setQueryData(['estimate', estimate.id], updated);
        queryClient.invalidateQueries({ queryKey: ['estimates'] });
    };

    return {
        target: { kind: 'estimate', id: estimate.id },
        companyId: estimate.company_id,
        documentNumber: estimate.estimate_number,
        total: estimate.grand_total ?? 0,
        payments: estimate.payments ?? [],
        paymentStatus: estimate.payment_status,
        depositAmount: estimate.deposit_amount,
        saveDeposit: async (amount) => {
            // null means "unchanged" to the estimate endpoint, so 0 clears
            setEstimate(await apiService.updateEstimate(estimate.id, { deposit_amount: amount ?? 0 }));
        },
        receiptEmail: estimate.contacts?.find((c) => c.contact?.email)?.contact.email,
        // Estimates have no stored status; the server derives it from the payments.
        savePayments: async (ops) => {
            setEstimate(await apiService.updateEstimate(estimate.id, { payment_operations: ops }));
        },
        awaitProviderUpdate: () => {
            pollForPaymentChange(estimate.payments?.length ?? 0, () => apiService.getEstimateById(estimate.id), (fresh) => {
                setEstimate(fresh);
                queryClient.invalidateQueries({ queryKey: ['stripePayment'] });
            });
        },
    };
}
